import React from 'react';
import { useMediaQuery } from '@mui/material';
import { useSelector } from 'react-redux';
import { combineClassNames } from 'app/functions';
import {
    extractHrefFromHtmlLink,
    extractLinkLabelFromHtml,
    normalizeHotYoterHtmlContent,
} from '../utils/hotYoterHtml';

const sanitizeHtmlForRtl = (html) => html
    .replace(/\sdir=["']ltr["']/gi, ' dir="rtl"');

/**
 * Ultra-wide cards grow much taller than the decorative text panel in the
 * intro background. Cap synced height so page-info stays inside that panel.
 */
const getSyncedPageInfoHeight = (cardRowHeight) => {
    if (cardRowHeight == null || !Number.isFinite(cardRowHeight)) {
        return undefined;
    }

    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 0;

    if (viewportWidth >= 5120) {
        // Extra room for larger 5K page-info type.
        return Math.min(cardRowHeight, 560);
    }

    if (viewportWidth >= 3840) {
        // Extra room for larger 4K page-info type.
        return Math.min(cardRowHeight, 390);
    }

    if (viewportWidth >= 3440) {
        return Math.min(cardRowHeight, 340);
    }

    if (viewportWidth >= 2560) {
        return Math.min(cardRowHeight, 300);
    }

    return cardRowHeight;
};

const renderRichText = (content) => {
    const html = normalizeHotYoterHtmlContent(content);

    if (html && /<[a-z][\s\S]*>/i.test(html)) {
        return (
            <span
                dir="rtl"
                dangerouslySetInnerHTML={{ __html: sanitizeHtmlForRtl(html) }}
            />
        );
    }

    return content;
};

export const Content = ({
    children,
    className,
    testId,
    hasBackgroundImage = false,
}) => (
    <div
        className={combineClassNames(
            'hot-yoter-page-info__content',
            hasBackgroundImage && 'hot-yoter-page-info__content_has-bg',
            className,
        )}
        dir="rtl"
        testId={testId}
    >
        {children}
    </div>
);

export const HotYoterPageInfo = ({
    headerText,
    pageTitle,
    hasBackgroundImage = false,
    cardRowHeight = null,
    regulationsLink = null,
    regulationsLabel = null,
    onRegulationsPress,
    testId = 'hot-yoter-page-info',
}) => {
    const deviceState = useSelector((store) => store.state.deviceState);
    const isMobileViewport = useMediaQuery('(max-width:767px)');
    const isUltraWide = useMediaQuery('(min-width:2560px)');
    const isMobile = deviceState === 'mobile' || isMobileViewport;
    const title = headerText?.title ?? pageTitle;
    const subtitle = headerText?.subtitle ?? null;
    const linkUrl = extractHrefFromHtmlLink(headerText?.link)
        ?? headerText?.linkUrl
        ?? headerText?.regulationsUrl
        ?? null;
    const linkLabel = extractLinkLabelFromHtml(headerText?.link)
        ?? headerText?.linkLabel
        ?? 'לתקנון הטבות הוטיותר';
    const showDesktopRegulationsLink = Boolean(regulationsLink) && !isMobile;

    if (!title && !subtitle && !linkUrl && !showDesktopRegulationsLink) {
        return null;
    }

    const handleLinkPress = () => {
        if (!linkUrl) {
            return;
        }

        if (onRegulationsPress) {
            onRegulationsPress(linkUrl);
            return;
        }

        window.open(linkUrl, '_blank', 'noopener,noreferrer');
    };

    const syncCardRowHeight = !isMobile && cardRowHeight != null;
    const syncedHeight = syncCardRowHeight
        ? getSyncedPageInfoHeight(cardRowHeight)
        : undefined;

    return (
        <div
            className={combineClassNames(
                'hot-yoter-page-info',
                !isMobile && 'hot-yoter-page-info_desktop',
                hasBackgroundImage && 'hot-yoter-page-info_has-bg',
                syncCardRowHeight && 'hot-yoter-page-info_height-sync',
            )}
            style={syncedHeight != null ? (
                isUltraWide
                    ? {
                        height: syncedHeight,
                        minHeight: syncedHeight,
                        maxHeight: syncedHeight,
                    }
                    : { minHeight: syncedHeight }
            ) : undefined}
            testId={`${testId}-root`}
        >
            <Content
                testId={`${testId}-content`}
                hasBackgroundImage={hasBackgroundImage}
            >
                {title ? (
                    <div className="hot-yoter-page-info__title" testId={`${testId}-title`}>
                        {title}
                    </div>
                ) : null}
                {subtitle ? (
                    <div className="hot-yoter-page-info__subtitle" testId={`${testId}-subtitle`}>
                        {subtitle}
                    </div>
                ) : null}
                {showDesktopRegulationsLink ? (
                    <button
                        type="button"
                        className="hot-yoter-page-info__regulations-link"
                        onClick={() => onRegulationsPress?.(regulationsLink)}
                        testId={`${testId}-regulations-link`}
                    >
                        {regulationsLabel ?? linkLabel}
                    </button>
                ) : null}
            </Content>
            {linkUrl && isMobile ? (
                <button
                    type="button"
                    className="hot-yoter-page-info__link"
                    onClick={handleLinkPress}
                    testId={`${testId}-link`}
                >
                    {linkLabel}
                </button>
            ) : null}
        </div>
    );
};
